import type {
  FedSectionOrigin,
  GeneratedDimension,
  GeneratedItem,
  GeneratedLink,
  GeneratedStatus,
  GuidanceDocument, GuidanceVideo,
  InstanceDetail,
  NodeOrigin,
  ResolvedSegment,
  SpanningItem,
  SegmentRequirement,
  SegmentRollup,
} from "@keydials/api-client";
import { segmentRequirementsFor } from "@keydials/api-client";
import { bandPaletteColour } from "@keydials/dial-renderer";
import {
  daySlotStatus,
  deriveSegmentRollups,
  isCalendarEvent,
  lookupStatus,
  paletteForSegment,
  paletteOverrideForSegment,
  resolvedStatus,
  sectionEdgeSegmentsOf,
  sectionStatusOf,
  statusIndex,
  unionStatusByKey,
  type SectionEdgeSegment,
} from "./instanceModel";
import { isPastDate, RAG_COLOUR, utcDay, type BarPart, type Rag } from "./lib/ui";

// Re-export the bar-part shape used by callers without forcing a second import path.
export type { BarPart } from "./lib/ui";

const NONE_COLOUR = "#94a3b8"; // --st-none, for unset statuses / missing rollups

/** A segment (the design's "action point") prepared for rendering. */
export interface VSegment {
  key: string;
  label: string; // short code, e.g. "A1"
  name: string; // full display name
  description?: string | null;
  guidance?: string | null;
  /** External-resource links shown alongside this action point's guidance. */
  links?: GeneratedLink[] | null;
  /** Author-provided guidance videos for this action point (viewer-resolved playback URLs). */
  videos?: GuidanceVideo[] | null;
  /** Author-provided documents for this action point (viewer-resolved download URLs). */
  documents?: GuidanceDocument[] | null;
  items: GeneratedItem[];
  /** KEY-859: on a time of day, what's listed earlier that day but runs on into it (the server counts it here). */
  spanning?: SpanningItem[] | null;
  /**
   * True when this action point is scored by its ITEMS — i.e. the dial is Itemised *and* the segment has at
   * least one item. Until an itemised action point has its first item it behaves exactly like a Simple dial's
   * action point (KEY-259): its own status, its own picker, its own review date. Components branch on this
   * rather than on {@link DialVM.simple}, which is a whole-dial property.
   */
  itemActive: boolean;
  /** Fill for the dial arc, segment dot and chips: status colour (simple) or RAG band (itemised). */
  colour: string;
  // Simple dials carry status directly on the segment:
  statusKey?: string;
  status?: GeneratedStatus;
  palette: GeneratedStatus[]; // effective palette for a status change
  reviewDate?: string | null;
  /** Status this segment auto-reverts to when its review date passes (Phase 6); null = none scheduled. */
  revertTo?: string | null;
  /** This action point's own reminder horizon, overriding the dial's ladder (KEY-413); null follows it. */
  reviewReminderLeadDays?: number | null;
  reviewReminderRepeatDays?: number | null;
  /** How often this action point's review comes round again, or null for a one-off. */
  reviewRecurrenceUnit?: string | null;
  reviewRecurrenceCount?: number | null;
  overdue: boolean;
  // Leaf-item tally (drives "x / y complete" and the completion ring):
  done: number;
  total: number;
  /**
   * KEY-773: the calendar events inside {@link done} and {@link total} — counted in a slot's figures, never in the
   * dial's completion. A time of day holding an appointment says so ("1 / 2 appointments"), but an appointment is
   * not work the dial is waiting on, so the dial's own tallies take these back out. Absent where there are none.
   */
  eventsDone?: number;
  eventsTotal?: number;
  // Itemised dials roll up server-side:
  rollup?: SegmentRollup;
  band?: Rag;
  /**
   * For an item-scored action point: the status its display rule — or the dial's axis — resolved to, when
   * one did. The chip has always been COLOURED by this and labelled with the RAG band instead, so a segment
   * carrying a threshold ladder showed the ladder's colour under the words "Some complete" (KEY-485). Named
   * separately from {@link status}, which belongs to a segment that carries its own status.
   */
  displayStatus?: GeneratedStatus;
  // Group Key Dial (Phase 7): the child instance this overview segment drills into, if any.
  childInstanceId?: string | null;
  /** True for a Group overview segment (carries a rollup score + health tier and drills into a child). */
  isGroupSegment?: boolean;
  /** The child section this group segment stands for, so a drill-in can land on it: the mirrored section
   * on a mirror face, or the one section an authored link is bound to. Null when it rolls up a whole child. */
  childSectionKey?: string | null;
  /** Mirror-face groups: the single child segment this face segment is a window onto. */
  childSegmentKey?: string | null;
  // Group Key Dial composite scoring (KEY-162), server-computed:
  score?: number | null;
  classification?: string | null;
  /**
   * KEY-460: the fields this action point must have answered before its items can be worked on, that it
   * hasn't. Empty for every action point that requires nothing or has answered everything — which is
   * nearly all of them. While it is non-empty the Items tab is locked and the action point is flagged.
   *
   * Server-computed and taken as given: a field restricted away from this viewer is still answered, so a
   * client working the lock out from declarations and visible values would lock people out for ever.
   *
   * Optional so the decorative dials built by hand (the login backdrops, the group placeholder) don't have
   * to declare an empty list; absent and empty mean the same thing — nothing is being waited on.
   */
  missingRequiredFields?: SegmentRequirement[];
  // Customisation overlay tags (Phase D badging):
  origin?: NodeOrigin;
  overridden?: boolean;
  /** KEY-754: hidden on this dial and shown only because the dial admin looking asked to see the hidden parts
   * for a while. Badged "Hidden", and kept out of the dial's own tallies, which everyone else reads without it. */
  isHidden?: boolean;
}

/** A section (outer ring grouping). */
export interface VSection {
  key: string;
  label: string;
  description?: string | null;
  guidance?: string | null;
  /** External-resource links shown alongside this section's guidance. */
  links?: GeneratedLink[] | null;
  /** Author-provided guidance videos for the section (viewer-resolved playback URLs). */
  videos?: GuidanceVideo[] | null;
  /** Author-provided documents for the section (viewer-resolved download URLs). */
  documents?: GuidanceDocument[] | null;
  segments: VSegment[];
  done: number;
  total: number;
  /** When true, render the section by its short `key` (mnemonic) rather than its full `label`. */
  showMnemonic?: boolean;
  /** The section's own band colour (KEY-755), lower-case `#rrggbb`; null means the platform's section colour.
   * Purely presentational: the status edge is still drawn over it. */
  colour?: string | null;
  /** The section's own status (KEY-479), when the dial offers section statuses and one has been set. Shown
   * as a coloured edge on the section's wedge — never as its fill — and as a badge beside its name. */
  status?: GeneratedStatus;
  /** The same edge, cut into one arc per action point (KEY-494), when the dial has that switched on and
   * the status is derived rather than hand-set. Present means draw these INSTEAD of {@link status}'s
   * single arc; {@link status} still supplies the section's badge either way. */
  statusSegments?: SectionEdgeSegment[];
  // Customisation overlay tags (Phase D badging):
  origin?: NodeOrigin;
  overridden?: boolean;
  /** Whose work this is, when another organisation feeds it onto this dial (KEY-257 phase 3). Null for
   * the dial's own sections. A fed section is watched, not worked: it is excluded from every tally on
   * {@link DialVM} and must not offer any edit affordance. */
  fedFrom?: FedSectionOrigin | null;
  /** KEY-754: hidden on this dial and shown only because the dial admin looking asked to see the hidden parts
   * for a while. Badged "Hidden", and kept out of the dial's own tallies, which everyone else reads without it. */
  isHidden?: boolean;
}

/** The title to show for a section: its short `key` (mnemonic) when `showMnemonic`, else the full `label`. */
export function sectionTitle(section: Pick<VSection, "key" | "label" | "showMnemonic">): string {
  return section.showMnemonic ? section.key : section.label;
}

/** The whole dial, normalised so components never branch on Simple vs Itemised. */
export interface DialVM {
  detail: InstanceDetail;
  simple: boolean;
  sections: VSection[];
  dimensions: GeneratedDimension[];
  completion: number; // 0..1 across leaf items
  done: number;
  total: number;
  attention: number; // overdue segments (simple dials)
  itemCount: number; // leaf-item count, for "Simple · N items"
  sectionCount: number;
  /** True for a Group Key Dial: segments are coloured by a rollup of child instances and drill into them. */
  isGroup: boolean;
}


/** Build the dial view model from an instance detail (and its server rollup). */
export function buildDialVM(detail: InstanceDetail): DialVM {
  const itemised = detail.kind === "Itemised";
  const index = statusIndex(detail);
  const union = unionStatusByKey(detail);
  const statusMeta = new Map(detail.statuses.map((s) => [`${s.sectionKey}\0${s.segmentKey}`, s]));
  // Itemised dials carry a server rollup — except for a scoped viewer, to whom it's withheld; derive the
  // per-segment bands from the visible item statuses in that case (see deriveSegmentRollups).
  const rollupByKey = new Map(
    (detail.rollup?.segments ?? (itemised ? deriveSegmentRollups(detail) : [])).map(
      (s) => [`${s.sectionKey}\0${s.segmentKey}`, s],
    ),
  );
  // Once a rollup exists, a segment missing from it is one the rollup deliberately scores nothing for —
  // no items, or every item N/A — so it reads 0/0 rather than falling back to its raw item count. Without
  // a rollup at all (a dimensionless itemised dial) the raw count is still the best total available.
  const hasRollup = detail.rollup != null || rollupByKey.size > 0;

  // Group Key Dial (Phase 7): overview segments are coloured by a server-computed rollup of their child
  // instances and drill into them, rather than carrying their own status.
  const isGroup = !!detail.isGroup;
  const overviewByKey = new Map((detail.overview ?? []).map((o) => [`${o.sectionKey}\0${o.segmentKey}`, o]));

  let dialDone = 0;
  let dialTotal = 0;
  // KEY-773: an event's status, by item, on the check its day is coloured by — stored or implied by the server.
  const itemStatusByKey = new Map<string, string>(
    (detail.itemStatuses ?? []).map(
      (s) => [`${s.sectionKey}\0${s.segmentKey}\0${s.itemKey}\0${s.dimensionKey}`, s.statusKey] as const,
    ),
  );
  let attention = 0;
  let itemCount = 0;

  const sections: VSection[] = detail.sections.map((section) => {
    // A section another organisation feeds onto this dial (KEY-257 phase 3) is watched, not worked. It
    // stays OUT of every tally below — completion, attention and the item count — because a dial's
    // compliance figure has to be about that dial's own work. Left in, a builder finishing their part
    // would silently move the homeowner's percentage, and nobody would know where the number came from.
    const fed = section.fedFrom ?? null;
    // KEY-754: a hidden part, present only because a dial admin asked to see it, stays out of the tallies too —
    // the figure has to be the one everybody else sees, not one that moves while the admin is looking.
    const sectionHidden = !!section.isHidden;
    // KEY-773: a day coloured from its items scores its events on that check; a busyness day leaves them all open.
    const slotCheck = section.slotColouring?.dimensionKey;
    const slotCheckTerminal = new Set(
      (slotCheck ? detail.dimensions?.find((d) => d.key === slotCheck)?.palette.statuses ?? [] : [])
        .filter((s) => s.isTerminal)
        .map((s) => s.key),
    );

    const segments: VSegment[] = section.segments.map((segment) => {
      const slot = `${section.key}\0${segment.key}`;
      const hidden = sectionHidden || !!segment.isHidden;
      if (!fed && !hidden) {
        itemCount += itemised ? segment.items.filter((i) => !i.isHidden).length : 1;
      }

      // KEY-259: an itemised action point is only *scored by its items* once it HAS one. An empty one falls
      // through to the Simple branch below and carries its own status — same colour, picker and review date a
      // Simple dial's action point would have — so authors aren't stuck with an inert grey "No items" node.
      // KEY-574: never on a group. A group's wedge is a window onto a CHILD — coloured and scored by the
      // server's overview and drilled into — and a Mirror face copies the child's sections verbatim, items
      // included. Taking the item branch there scored the wedge by the group's own (empty) item rollup and
      // dropped its child link, so on an itemised Mirror group neither the wedge nor its section drilled.
      // KEY-771: a time of day on a days section coloured from its items is scored by them even while empty — the
      // server colours it (no colour, or the section's empty-slot status) and drops any hand-set status, so offering
      // the dial-default picker there only ever set something nobody would see.
      // KEY-859: so is one with something running on into it from earlier in the day — it lists that, faintly.
      const itemActive = itemised && !isGroup && (segment.items.length > 0 || (!!section.slotColouring && !!segment.date)
        || ((segment as ResolvedSegment).spanning?.length ?? 0) > 0);

      // KEY-460: resolved once here rather than in each of the two branches below, because a lock is a
      // property of the action point and has nothing to do with how it happens to be scored.
      const missingRequiredFields = segmentRequirementsFor(detail, section.key, segment.key);

      if (itemActive) {
        const rollup = rollupByKey.get(slot);
        const band = rollup?.band;
        // KEY-773: the rollup scores only the dial's own items — calendar events are kept out of it, server-side and
        // in deriveSegmentRollups — so a slot holding an appointment read "0 items". Events are added back here, for
        // this slot's figures only; the dial's tallies below take them out again.
        const own = segment.items.filter((i) => !isCalendarEvent(i));
        const events = segment.items.filter((i) => isCalendarEvent(i) && (hidden || !i.isHidden));
        const eventsDone = slotCheck
          ? events.filter((i) => {
            const statusKey = itemStatusByKey.get(`${slot}\0${i.key}\0${slotCheck}`);
            return statusKey !== undefined && slotCheckTerminal.has(statusKey);
          }).length
          : 0;
        const done = (rollup?.completeItems ?? 0) + eventsDone;
        const total = (rollup?.totalItems ?? (hasRollup ? 0 : own.length)) + events.length;
        // A display rule wins when it resolved a status; otherwise a per-segment palette override tints the
        // segment by completion band (KEY-187), and failing that the segment keeps the default RAG colour.
        const override = paletteOverrideForSegment(detail, segment);
        // KEY-771: a day's time of day takes the colour the server gave it (busyness, or its items), never the rollup.
        const daySlot = daySlotStatus(detail, section, segment, section.key);
        const ruled = daySlot !== undefined ? daySlot ?? undefined : resolvedStatus(detail, segment, rollup);
        return {
          key: segment.key,
          label: segment.label,
          name: segment.name,
          description: segment.description,
          guidance: segment.guidance,
          links: segment.links,
          videos: segment.videos,
          documents: segment.documents,
          items: segment.items,
          spanning: (segment as ResolvedSegment).spanning ?? null,
          itemActive: true,
          colour: daySlot !== undefined
            ? daySlot?.colour ?? NONE_COLOUR
            : ruled?.colour ?? (band ? (override ? bandPaletteColour(band, override) : RAG_COLOUR[band]) : NONE_COLOUR),
          palette: paletteForSegment(detail, segment),
          done,
          total,
          eventsDone: events.length > 0 ? eventsDone : undefined,
          eventsTotal: events.length > 0 ? events.length : undefined,
          rollup,
          band,
          displayStatus: ruled,
          overdue: false,
          missingRequiredFields,
          origin: segment.origin,
          overridden: segment.overridden,
          isHidden: hidden,
        };
      }

      // Simple (and item-less itemised action points): status lives on the segment.
      const statusKey = lookupStatus(index, section.key, segment.key);
      const palette = paletteForSegment(detail, segment);
      const status = statusKey ? palette.find((s) => s.key === statusKey) ?? union.get(statusKey) : undefined;
      const meta = statusMeta.get(slot);
      const reviewDate = meta?.reviewDate ?? null;
      const revertTo = meta?.revertToStatusKey ?? null;
      const reviewReminderLeadDays = meta?.reviewReminderLeadDays ?? null;
      const reviewReminderRepeatDays = meta?.reviewReminderRepeatDays ?? null;
      const reviewRecurrenceUnit = meta?.reviewRecurrenceUnit ?? null;
      const reviewRecurrenceCount = meta?.reviewRecurrenceCount ?? null;
      const isTerminal = !!status?.isTerminal;
      const overdue = !isTerminal && isPastDate(utcDay(reviewDate));
      // For a group's overview segment the colour comes from the server-computed rollup band — or, on a
      // Mirror-face group, the mirrored child segment's REAL status colour (statusColour) — and the
      // segment drills into its child instance instead of opening a status changer.
      const ov = isGroup ? overviewByKey.get(slot) : undefined;
      const groupBand = ov?.band ?? undefined;
      // KEY-509: the status this wedge is SHOWING — the worst its child action points display, once the
      // version colours its segments from its children, or a Mirror face's windowed child status. Synthesised
      // the way a rolled-up section edge is (`sectionStatusOf`): the group's own palette cannot hold a status
      // its children grade in, so the badge and the legend take name and colour from the entry itself.
      const groupStatus: GeneratedStatus | undefined = isGroup && ov?.statusKey
        ? {
          key: ov.statusKey,
          name: ov.statusName ?? ov.statusKey,
          colour: ov.statusColour ?? NONE_COLOUR,
          order: 0,
          allowedRoles: [],
          isTerminal: false,
        }
        : undefined;
      return {
        key: segment.key,
        label: segment.label,
        name: segment.name,
        description: segment.description,
        guidance: segment.guidance,
        links: segment.links,
        videos: segment.videos,
        documents: segment.documents,
        items: segment.items,
        itemActive: false,
        colour: isGroup
          ? ov?.statusColour ?? (groupBand ? RAG_COLOUR[groupBand] : NONE_COLOUR)
          : status?.colour ?? NONE_COLOUR,
        statusKey,
        status,
        palette,
        reviewDate,
        revertTo,
        reviewReminderLeadDays,
        reviewReminderRepeatDays,
        reviewRecurrenceUnit,
        reviewRecurrenceCount,
        overdue,
        // An item-less itemised action point stays OUT of the tallies (0/0), so the dial's done/total/
        // completion/itemCount remain purely item-based — its manual status colours the wedge without
        // moving the completion ring. A Simple dial's action point IS the unit of completion, so it counts.
        done: itemised ? 0 : isGroup ? (groupBand === "Green" ? 1 : 0) : isTerminal ? 1 : 0,
        total: itemised ? 0 : 1,
        band: groupBand,
        displayStatus: groupStatus,
        childInstanceId: ov?.childInstanceId ?? null,
        isGroupSegment: isGroup,
        childSectionKey: ov?.childSectionKey ?? null,
        childSegmentKey: ov?.childSegmentKey ?? null,
        score: ov?.score ?? null,
        classification: ov?.classification ?? null,
        missingRequiredFields,
        origin: segment.origin,
        overridden: segment.overridden,
        isHidden: hidden,
      };
    });

    // A hidden section shows its own figures; a visible one leaves out any action point hidden inside it.
    const counted = sectionHidden ? segments : segments.filter((s) => !s.isHidden);
    const done = counted.reduce((n, s) => n + s.done, 0);
    const total = counted.reduce((n, s) => n + s.total, 0);
    if (!fed && !sectionHidden) {
      // A day's header agrees with its slots, events and all; the dial's completion is about its own items only.
      dialDone += done - counted.reduce((n, s) => n + (s.eventsDone ?? 0), 0);
      dialTotal += total - counted.reduce((n, s) => n + (s.eventsTotal ?? 0), 0);
      attention += segments.filter((s) => s.overdue).length;
    }

    return {
      key: section.key,
      fedFrom: fed,
      label: section.label,
      description: section.description,
      guidance: section.guidance,
      links: section.links,
      videos: section.videos,
      documents: section.documents,
      segments,
      done,
      total,
      showMnemonic: section.showMnemonic,
      colour: section.colour ?? null,
      status: fed ? undefined : sectionStatusOf(detail, section.key),
      // A fed section shows no status of its own, so it has nothing to split either.
      statusSegments: fed ? undefined : sectionEdgeSegmentsOf(detail, section.key),
      origin: section.origin,
      overridden: section.overridden,
      isHidden: sectionHidden,
    };
  });

  // Itemised dials carry an authoritative whole-dial rollup; simple dials — and scoped itemised viewers
  // with no server rollup — derive it from the per-segment tallies above (visible segments only).
  const completion = itemised
    ? detail.rollup?.completion ?? (dialTotal > 0 ? dialDone / dialTotal : 0)
    : dialTotal > 0
      ? dialDone / dialTotal
      : 0;
  const done = itemised ? detail.rollup?.completeItems ?? dialDone : dialDone;
  const total = itemised ? detail.rollup?.totalItems ?? dialTotal : dialTotal;

  return {
    detail,
    simple: !itemised,
    sections,
    dimensions: [...(detail.dimensions ?? [])].sort((a, b) => a.order - b.order),
    completion,
    done,
    total,
    attention,
    itemCount,
    // Own sections only — the same reasoning as the tallies above. "3 sections" should not become "4"
    // because somebody else is feeding you one.
    sectionCount: sections.filter((s) => !s.fedFrom).length,
    isGroup,
  };
}

/**
 * Where a click on a Group Key Dial's face should land: the child instance to open and, when the clicked
 * node stands for exactly one part of it, the child section (and action point) to open there.
 */
export interface DrillTarget {
  childInstanceId: string;
  /** The child section to open, or null to land on the child's overview. */
  sectionKey: string | null;
  /** The child action point to open within {@link sectionKey}, or null to stop at the section. */
  segmentKey: string | null;
}

/**
 * The drill-in target for one segment of a group's face, or null when the segment isn't a group segment
 * (so it opens its own action-point panel as usual). A mirror-face segment is a 1:1 window onto one child
 * action point, so it targets both keys; an authored segment bound to one child section (KEY-153) targets
 * the section; a whole-dial link targets neither and lands on the child's overview.
 */
export function drillTargetForSegment(vm: DialVM, sectionKey: string, segmentKey: string): DrillTarget | null {
  const segment = vm.sections.find((s) => s.key === sectionKey)?.segments.find((sg) => sg.key === segmentKey);
  if (!segment?.childInstanceId) {
    return null;
  }
  return {
    childInstanceId: segment.childInstanceId,
    sectionKey: segment.childSectionKey ?? null,
    segmentKey: segment.childSegmentKey ?? null,
  };
}

/**
 * The drill-in target for a whole section of a group's face, or null when the section spans several child
 * dials (or none) and so has no single dial to open. A mirror face gives one section per child section, so
 * a whole section IS one dial; an authored overview section qualifies too when every LINKED segment in it
 * maps to the same child. The child section is only targeted when those segments agree on one — and never
 * an action point, since a section spans many.
 *
 * KEY-574: an unlinked segment (a slot not wired to a child yet, or whose child isn't allocated) has no say.
 * It has nowhere to go, and letting it veto the section sent a click on one child's section to the group's
 * own Section panel instead of that child.
 */
export function drillTargetForSection(vm: DialVM, sectionKey: string): DrillTarget | null {
  const linked = (vm.sections.find((s) => s.key === sectionKey)?.segments ?? []).filter((sg) => !!sg.childInstanceId);
  const childInstanceId = linked[0]?.childInstanceId;
  if (!childInstanceId || !linked.every((sg) => sg.childInstanceId === childInstanceId)) {
    return null;
  }
  const childSectionKeys = new Set(linked.map((sg) => sg.childSectionKey ?? null));
  return {
    childInstanceId,
    sectionKey: childSectionKeys.size === 1 ? [...childSectionKeys][0] : null,
    segmentKey: null,
  };
}

/**
 * True when the dial has a MIX: at least one empty (zero-item) segment {@link hideEmptyNodes} could hide,
 * and at least one that would survive it. Only itemised dials carry items, so a simple dial is never
 * "empty" in this sense.
 *
 * The upper bound is not fussiness — it stops the filter being offered where it would delete the dial.
 * {@link hideEmptyNodes} drops every empty segment and then every section left with none, so on a face
 * that is empty throughout it returns `sections: []`; DialView then divides 2π by `sections.length` and
 * draws NaN geometry, i.e. a blank circle with no way back to it except the switch that emptied it. An
 * authored-face GROUP hits this squarely: its face sections are its own authored structure and carry no
 * items at all, while its kind is still Itemised. Mobile reached the same rule independently and for the
 * same reason — see `src/mobile/src/api/dialMap.ts`, which offers its filter only when
 * `emptyCount > 0 && emptyCount < allSegments.length`.
 */
export function hasEmptyNodes(vm: DialVM): boolean {
  if (vm.simple) {
    return false;
  }
  const segments = vm.sections.flatMap((s) => s.segments);
  const empty = segments.filter((seg) => seg.items.length === 0).length;
  return empty > 0 && empty < segments.length;
}

/**
 * A copy of the VM with empty (zero-item) segments removed, and any section thereby left with no
 * segments dropped too. Item-based aggregates (done/total/completion/itemCount) are unchanged because
 * an empty segment contributes nothing to them; only the section list and count shrink. Returns the VM
 * untouched for simple dials (which have no items), and for a face that would be filtered away entirely.
 */
export function hideEmptyNodes(vm: DialVM): DialVM {
  if (vm.simple) {
    return vm;
  }
  const sections = vm.sections
    .map((section) => ({ ...section, segments: section.segments.filter((seg) => seg.items.length > 0) }))
    .filter((section) => section.segments.length > 0);
  // Belt and braces for the rule hasEmptyNodes enforces at the point of offer: never hand back a dial with
  // no sections left. This function is exported, and a caller that reaches it without asking first would
  // otherwise get a blank dial rather than a filtered one. Showing everything is the safe answer.
  if (sections.length === 0) {
    return vm;
  }
  return { ...vm, sections, sectionCount: sections.length };
}

/** Proportional status-bar parts for a dashboard card: RAG bands (itemised) or status counts (simple). */
export function dialBarParts(vm: DialVM): BarPart[] {
  if (vm.simple) {
    const byStatus = new Map<string, { colour: string; weight: number; order: number; terminal: boolean; label: string }>();
    for (const section of vm.sections) {
      for (const seg of section.segments) {
        const key = seg.statusKey ?? "__unset";
        const existing = byStatus.get(key);
        if (existing) {
          existing.weight += 1;
        } else {
          byStatus.set(key, {
            colour: seg.colour,
            weight: 1,
            order: seg.status?.order ?? -1,
            terminal: !!seg.status?.isTerminal,
            label: seg.status?.name ?? "Not set",
          });
        }
      }
    }
    return [...byStatus.entries()]
      .map(([key, v]) => ({ key, colour: v.colour, weight: v.weight, label: v.label, order: v.order, terminal: v.terminal }))
      .sort((a, b) => Number(b.terminal) - Number(a.terminal) || b.order - a.order)
      .map(({ key, colour, weight, label }) => ({ key, colour, weight, label }));
  }

  // Itemised: count segments in each RAG band, complete-first.
  const bands: { key: Rag; weight: number }[] = [
    { key: "Green", weight: 0 },
    { key: "Amber", weight: 0 },
    { key: "Red", weight: 0 },
  ];
  for (const section of vm.sections) {
    for (const seg of section.segments) {
      const band = bands.find((b) => b.key === seg.band);
      if (band) {
        band.weight += 1;
      }
    }
  }
  return bands
    .filter((b) => b.weight > 0)
    .map((b) => ({ key: b.key, colour: RAG_COLOUR[b.key], weight: b.weight, label: b.key }));
}
